/**
 * The scene <-> GameSession bridge: the ONE convention every feature scene uses to
 * (a) read the live {@link GameSession} off the Phaser registry and (b) build the
 * {@link GameSessionDeps} the session needs to reach the LLM proxy.
 *
 * The session itself is created by the entry flow (Title resumes it, NewGame mints
 * it) and stashed on the registry under {@link REGISTRY_SESSION}. Feature scenes
 * never construct a session — they only read it:
 *
 *   import { getSession } from '@/scenes/session';
 *   const session = getSession(this);          // throws only if reached pre-entry
 *
 * The deps (proxy base URL + bearer token) are resolved here, in ONE place, so the
 * entry scenes stay declarative and the api-base URL is env-configurable (localhost
 * in dev, the GCP origin in prod) exactly as CLAUDE.md requires. Secrets never ship
 * in the bundle: the token is the user-entered one the auth gate stored in
 * localStorage; absent it, the session quietly runs on the TemplateVoice floor.
 */
import Phaser from 'phaser';
import type { GameSession, GameSessionDeps } from '@/session';
import { Autosave } from '@/session';
import { readProxyToken, PROXY_TOKEN_KEY } from '@/store';
import { REGISTRY_SESSION } from './PreloadScene.js';

/** localStorage key the password/auth gate stores the proxy bearer token under. */
export { PROXY_TOKEN_KEY };

/** Registry key the running {@link Autosave} loop is stashed under (one per game). */
const REGISTRY_AUTOSAVE = 'pr:autosave';

/**
 * Read the live GameSession from the registry. Returns null before the entry flow
 * has set it (so callers in the entry flow itself can probe without throwing).
 */
export function peekSession(scene: Phaser.Scene): GameSession | null {
  return (scene.registry.get(REGISTRY_SESSION) as GameSession | undefined) ?? null;
}

/**
 * Read the live GameSession, asserting it exists. Feature scenes are only ever
 * reached *after* Title/NewGame set it, so a miss is a wiring bug, not a runtime
 * state — fail loudly in that one case rather than render an empty world.
 */
export function getSession(scene: Phaser.Scene): GameSession {
  const session = peekSession(scene);
  if (!session) {
    throw new Error(
      'getSession: no GameSession on the registry — a feature scene was started ' +
        'before the Title/NewGame entry flow created it.',
    );
  }
  return session;
}

/**
 * Store the live session on the registry (the entry flow's single write point) AND
 * (re)start the centralized {@link Autosave} bound to it — a ~15s heartbeat plus a
 * flush on tab hide/close — so the owner never loses progress without manually saving.
 *
 * Any autosave from a prior session (a previous New Game / Continue) is stopped first,
 * tearing down its timer + every listener — the Phaser-scene-reuse safety so loops and
 * handlers never stack. Saving is non-blocking and never throws.
 */
export function setSession(scene: Phaser.Scene, session: GameSession): void {
  stopAutosave(scene);
  scene.registry.set(REGISTRY_SESSION, session);

  const autosave = new Autosave(session);
  scene.registry.set(REGISTRY_AUTOSAVE, autosave);
  autosave.start();
}

/**
 * Stop + clear the running autosave (if any) and the session from the registry — the
 * "session ended / swapped" teardown. Idempotent and non-throwing; does not itself
 * save (the live autosave's hide/close hook already covers tab close).
 */
export function clearSession(scene: Phaser.Scene): void {
  stopAutosave(scene);
  scene.registry.set(REGISTRY_SESSION, undefined);
}

/** Tear down any autosave loop currently on the registry. Idempotent, non-throwing. */
function stopAutosave(scene: Phaser.Scene): void {
  const prior = scene.registry.get(REGISTRY_AUTOSAVE) as Autosave | undefined;
  if (prior) {
    try {
      prior.stop();
    } catch {
      // stop() is guarded internally; this is belt-and-braces so a teardown never throws.
    }
    scene.registry.set(REGISTRY_AUTOSAVE, undefined);
  }
}

/**
 * The proxy base URL: env-configurable (`VITE_API_BASE_URL`), defaulting to the local
 * dev proxy. Empty string => same-origin (the typical prod shape behind nginx).
 */
export function apiBaseUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const configured = env?.VITE_API_BASE_URL;
  if (configured != null) return configured;
  // Dev default: the local Fastify proxy on the Mac.
  if (env?.DEV) return 'http://localhost:8787';
  // Prod default: same origin (nginx fronts the proxy).
  return '';
}

/** The user-entered proxy bearer token, if the auth gate has stored one. */
export function proxyToken(): string | null {
  return readProxyToken();
}

/**
 * The wiring the proxy password gate ({@link import('@/store').authenticate} /
 * {@link import('@/store').probeAuthPosture}) needs: just the env-configured base URL.
 * No token here — the gate is what MINTS the token.
 */
export function authDeps(): { apiBaseUrl: string } {
  return { apiBaseUrl: apiBaseUrl() };
}

/**
 * Assemble the GameSessionDeps from the runtime environment. This is what the entry
 * scenes pass to {@link GameSession.loadOrNew} / {@link GameSession.createNew}; the
 * session uses it to (best-effort) reach the proxy, degrading to the template floor
 * when the token/base/probe is absent.
 */
export function sessionDeps(): GameSessionDeps {
  const token = proxyToken();
  return {
    apiBaseUrl: apiBaseUrl(),
    proxyToken: token,
  };
}
